import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

const formatDateInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getDefaultPeriod = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 6);
  return {
    startDate: formatDateInput(start),
    endDate: formatDateInput(end),
  };
};

const formatDateTimeBR = (value) => {
  if (!value) return "-";
  const raw = String(value).trim();
  const withoutTimezone = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}$/;
  const normalized = withoutTimezone.test(raw)
    ? `${raw.replace(" ", "T")}Z`
    : raw;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
  });
};

const toTimestampMs = (value) => {
  if (!value) return 0;
  const raw = String(value).trim();
  const withoutTimezone = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}$/;
  const normalized = withoutTimezone.test(raw)
    ? `${raw.replace(" ", "T")}Z`
    : raw;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const eventLabelMap = {
  "email.sent": "Enviado",
  "email.delivered": "Entregue",
  "email.opened": "Aberto",
  "email.clicked": "Clique",
  "email.bounced": "Rejeitado",
  "email.complained": "Spam",
  "email.failed": "Falhou",
  "email.delivery_delayed": "Atrasado",
  "email.suppressed": "Suprimido",
};

const eventPriority = {
  "email.sent": 1,
  "email.delivered": 2,
  "email.opened": 3,
  "email.clicked": 4,
  "email.delivery_delayed": 5,
  "email.suppressed": 6,
  "email.bounced": 7,
  "email.complained": 8,
  "email.failed": 9,
};

const eventBadgeStyles = {
  "email.sent": {
    dot: "#98a2b3",
    bg: "linear-gradient(180deg, #344054 0%, #2b394f 100%)",
    border: "rgba(152,162,179,0.45)",
    text: "#eaf0ff",
  },
  "email.delivered": {
    dot: "#22c55e",
    bg: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)",
    border: "rgba(134,239,172,0.45)",
    text: "#ecfdf3",
  },
  "email.opened": {
    dot: "#22d3ee",
    bg: "linear-gradient(180deg, #0e7490 0%, #155e75 100%)",
    border: "rgba(103,232,249,0.45)",
    text: "#ecfeff",
  },
  "email.clicked": {
    dot: "#60a5fa",
    bg: "linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%)",
    border: "rgba(147,197,253,0.45)",
    text: "#eff6ff",
  },
  "email.bounced": {
    dot: "#f87171",
    bg: "linear-gradient(180deg, #dc2626 0%, #b91c1c 100%)",
    border: "rgba(252,165,165,0.45)",
    text: "#fff1f2",
  },
  "email.complained": {
    dot: "#f59e0b",
    bg: "linear-gradient(180deg, #d97706 0%, #b45309 100%)",
    border: "rgba(252,211,77,0.45)",
    text: "#fffbeb",
  },
  "email.failed": {
    dot: "#fb7185",
    bg: "linear-gradient(180deg, #e11d48 0%, #be123c 100%)",
    border: "rgba(251,113,133,0.45)",
    text: "#fff1f2",
  },
  "email.delivery_delayed": {
    dot: "#fbbf24",
    bg: "linear-gradient(180deg, #a16207 0%, #854d0e 100%)",
    border: "rgba(253,224,71,0.45)",
    text: "#fefce8",
  },
  "email.suppressed": {
    dot: "#9ca3af",
    bg: "linear-gradient(180deg, #4b5563 0%, #374151 100%)",
    border: "rgba(209,213,219,0.35)",
    text: "#f9fafb",
  },
  default: {
    dot: "#cbd5e1",
    bg: "linear-gradient(180deg, #334155 0%, #1e293b 100%)",
    border: "rgba(148,163,184,0.35)",
    text: "#e2e8f0",
  },
};

const WebhookEmailCenter = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const defaults = useMemo(() => getDefaultPeriod(), []);
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [overview, setOverview] = useState(null);
  const [eventsData, setEventsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [checklistVisible, setChecklistVisible] = useState(false);
  const [checklistRanAt, setChecklistRanAt] = useState(null);
  const [groupByMessage, setGroupByMessage] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  const loadData = async (nextPage = page) => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        page: String(nextPage),
        limit: "20",
        type: typeFilter,
      });

      const [overviewResponse, eventsResponse] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_API_BASE_URL}/users/discount-campaign/webhook-overview?${params.toString()}`
        ),
        fetch(
          `${process.env.REACT_APP_API_BASE_URL}/users/discount-campaign/webhook-events?${params.toString()}`
        ),
      ]);

      const [overviewRaw, eventsRaw] = await Promise.all([
        overviewResponse.text(),
        eventsResponse.text(),
      ]);

      const overviewJson = overviewRaw ? JSON.parse(overviewRaw) : {};
      const eventsJson = eventsRaw ? JSON.parse(eventsRaw) : {};

      if (!overviewResponse.ok) {
        throw new Error(overviewJson?.message || "Falha ao carregar visão geral.");
      }
      if (!eventsResponse.ok) {
        throw new Error(eventsJson?.message || "Falha ao carregar eventos.");
      }

      setOverview(overviewJson);
      setEventsData(eventsJson);
      setPage(nextPage);
    } catch (requestError) {
      setError(requestError?.message || "Não foi possível carregar os dados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, typeFilter]);

  const applyPreset = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    setStartDate(formatDateInput(start));
    setEndDate(formatDateInput(end));
  };

  const totals = overview?.totals || {};
  const deliveredCount = Number(totals.delivered || 0);
  const openedCount = Number(totals.opened || 0);
  const clickedCount = Number(totals.clicked || 0);
  const shouldWarnTracking =
    deliveredCount >= 10 && openedCount === 0 && clickedCount === 0;
  const checklistItems = useMemo(
    () => [
      {
        label: "Webhook configurado no backend",
        ok: Boolean(overview?.webhookConfigured),
        okText: "OK",
        failText: "Pendente",
      },
      {
        label: "Existem e-mails entregues no período",
        ok: deliveredCount > 0,
        okText: "OK",
        failText: "Sem entregas",
      },
      {
        label: "Eventos de abertura recebidos",
        ok: openedCount > 0,
        okText: `${openedCount} evento(s)`,
        failText: "Nenhum evento",
      },
      {
        label: "Eventos de clique recebidos",
        ok: clickedCount > 0,
        okText: `${clickedCount} evento(s)`,
        failText: "Nenhum evento",
      },
    ],
    [overview?.webhookConfigured, deliveredCount, openedCount, clickedCount]
  );
  const totalPages = Math.max(
    1,
    Math.ceil((Number(eventsData?.total || 0) || 0) / Number(eventsData?.limit || 20))
  );
  const parsePayload = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;
    if (typeof value !== "string") return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };
  const getReliableEventTime = (row) => {
    const payload = parsePayload(row?.payload_json);
    return (
      payload?.created_at ||
      payload?.data?.open?.timestamp ||
      payload?.data?.click?.timestamp ||
      row?.event_created_at ||
      row?.created_at ||
      null
    );
  };
  const getReliableEventTimeMs = (row) => toTimestampMs(getReliableEventTime(row));
  const displayedRows = useMemo(() => {
    const rows = eventsData?.rows || [];
    if (!groupByMessage) return rows;

    const grouped = new Map();
    rows.forEach((row) => {
      const key =
        row.email_id ||
        `${row.recipient_email || "sem_email"}::${row.subject || "sem_assunto"}::${row.id}`;
      const currentDate = getReliableEventTimeMs(row);
      const current = grouped.get(key);

      if (!current) {
        grouped.set(key, {
          ...row,
          group_key: key,
          event_types: [row.event_type].filter(Boolean),
          row_count: 1,
          grouped_events: [
            {
              id: row.id,
              event_type: row.event_type,
              event_created_at: row.event_created_at,
              created_at: row.created_at,
              payload_json: row.payload_json,
              webhook_message_id: row.webhook_message_id,
            },
          ],
        });
        return;
      }

      const previousDate = getReliableEventTimeMs(current);

      grouped.set(key, {
        ...current,
        event_created_at: currentDate >= previousDate ? row.event_created_at : current.event_created_at,
        created_at: currentDate >= previousDate ? row.created_at : current.created_at,
        payload_json: currentDate >= previousDate ? row.payload_json : current.payload_json,
        recipient_email: current.recipient_email || row.recipient_email || null,
        subject: current.subject || row.subject || null,
        event_types: Array.from(
          new Set([...(current.event_types || []), row.event_type].filter(Boolean))
        ),
        row_count: (current.row_count || 1) + 1,
        grouped_events: [
          ...(current.grouped_events || []),
          {
            id: row.id,
            event_type: row.event_type,
            event_created_at: row.event_created_at,
            created_at: row.created_at,
            payload_json: row.payload_json,
            webhook_message_id: row.webhook_message_id,
          },
        ],
      });
    });

    return Array.from(grouped.values())
      .map((item) => ({
        ...item,
        grouped_events: [...(item.grouped_events || [])].sort((a, b) => {
          const d1 = getReliableEventTimeMs(a);
          const d2 = getReliableEventTimeMs(b);
          return d2 - d1;
        }),
      }))
      .sort((a, b) => {
        const d1 = getReliableEventTimeMs(a);
        const d2 = getReliableEventTimeMs(b);
        return d2 - d1;
      });
  }, [eventsData?.rows, groupByMessage]);
  const orderedEventTypes = (types = []) =>
    [...types].sort(
      (a, b) => (eventPriority[a] || 999) - (eventPriority[b] || 999)
    );
  const getRowEvents = (item) => {
    if (groupByMessage) return item.grouped_events || [];
    return [
      {
        id: item.id,
        event_type: item.event_type,
        event_created_at: item.event_created_at,
        created_at: item.created_at,
        payload_json: item.payload_json,
        webhook_message_id: item.webhook_message_id,
      },
    ];
  };
  const openDetails = (item) => {
    setSelectedRow(item);
    setShowTechnicalDetails(false);
  };
  const renderInfo = (label, value, options = {}) => (
    <Stack spacing={0.35}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          wordBreak: options.breakAll ? "break-all" : "break-word",
          fontFamily: options.mono ? "monospace" : "inherit",
        }}
      >
        {value || "-"}
      </Typography>
    </Stack>
  );
  const renderEventBadges = (item) => (
    <Stack
      direction="row"
      spacing={0.5}
      flexWrap="nowrap"
      useFlexGap
      sx={{ py: 0.125, overflowX: "auto", maxWidth: "100%" }}
    >
      {orderedEventTypes(groupByMessage ? item.event_types || [] : [item.event_type]).map(
        (eventType) => {
          const style = eventBadgeStyles[eventType] || eventBadgeStyles.default;
          return (
            <Box
              key={`${item.group_key || item.id}-${eventType}`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 0.85,
                py: 0.2,
                borderRadius: "999px",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.1,
                lineHeight: 1.1,
                whiteSpace: "nowrap",
                color: style.text,
                border: `1px solid ${style.border}`,
                background: style.bg,
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
              }}
            >
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  backgroundColor: style.dot,
                  boxShadow: `0 0 0 2px ${style.border}`,
                  flexShrink: 0,
                }}
              />
              {eventLabelMap[eventType] || eventType}
            </Box>
          );
        }
      )}
    </Stack>
  );

  return (
    <Box sx={{ width: "100%", px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1, sm: 2 } }}>
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Centro de Entregabilidade (Resend)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitore entrega, abertura, cliques e falhas reais dos e-mails da campanha.
            </Typography>

            {overview && !overview.webhookConfigured ? (
              <Alert severity="warning">
                Configure <strong>RESEND_WEBHOOK_SECRET</strong> no backend para ativar recebimento e validação do webhook.
              </Alert>
            ) : null}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                type="date"
                label="Data inicial"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                type="date"
                label="Data final"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                select
                label="Evento"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                fullWidth
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="email.delivered">Entregue</MenuItem>
                <MenuItem value="email.opened">Aberto</MenuItem>
                <MenuItem value="email.clicked">Clique</MenuItem>
                <MenuItem value="email.failed">Falhou</MenuItem>
                <MenuItem value="email.bounced">Rejeitado</MenuItem>
                <MenuItem value="email.complained">Spam</MenuItem>
                <MenuItem value="email.delivery_delayed">Atrasado</MenuItem>
                <MenuItem value="email.suppressed">Suprimido</MenuItem>
              </TextField>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label="Hoje" onClick={() => applyPreset(1)} />
              <Chip label="7 dias" onClick={() => applyPreset(7)} />
              <Chip label="15 dias" onClick={() => applyPreset(15)} />
              <Chip label="30 dias" onClick={() => applyPreset(30)} />
              <Chip
                label={groupByMessage ? "Visualização: Agrupado" : "Visualização: Por evento"}
                color={groupByMessage ? "primary" : "default"}
                onClick={() => setGroupByMessage((prev) => !prev)}
              />
              <Button variant="outlined" onClick={() => loadData(1)} disabled={loading}>
                Atualizar
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setChecklistVisible(true);
                  setChecklistRanAt(new Date());
                }}
              >
                Rodar checklist de tracking
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {checklistVisible ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Checklist de Tracking
              </Typography>
              {checklistRanAt ? (
                <Typography variant="caption" color="text.secondary">
                  Última execução: {checklistRanAt.toLocaleString("pt-BR")}
                </Typography>
              ) : null}

              {checklistItems.map((item) => (
                <Stack
                  key={item.label}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ py: 0.5 }}
                >
                  <Typography variant="body2">{item.label}</Typography>
                  <Chip
                    size="small"
                    color={item.ok ? "success" : "warning"}
                    label={item.ok ? item.okText : item.failText}
                  />
                </Stack>
              ))}

              <Alert severity="info">
                Teste recomendado: envie um e-mail para você, abra com imagens ativas e clique em um link. Depois
                clique em Atualizar para confirmar entrada dos eventos.
              </Alert>
            </Stack>
          </Paper>
        ) : null}

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <Paper variant="outlined" sx={{ p: 1.5, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Total de eventos
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {totals.total || 0}
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Mensagens únicas
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {totals.uniqueMessages || 0}
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Entregues
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {totals.delivered || 0}
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Aberturas (taxa)
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {totals.opened || 0} ({totals.openRate || 0}%)
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Cliques (taxa)
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {totals.clicked || 0} ({totals.clickRate || 0}%)
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Problemas
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {(totals.failed || 0) + (totals.bounced || 0) + (totals.complained || 0)}
            </Typography>
          </Paper>
        </Stack>

        {shouldWarnTracking ? (
          <Alert severity="warning">
            Tracking possivelmente inativo: {deliveredCount} entregues e zero aberturas/cliques no período.
            Valide `email.opened` e `email.clicked` no Resend e faça um teste manual.
          </Alert>
        ) : null}

        <Paper variant="outlined" sx={{ p: 0, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary">
              {groupByMessage
                ? "Visualização agrupada por mensagem: os status do mesmo envio aparecem juntos."
                : "Visualização por evento: o mesmo ID de mensagem pode aparecer em múltiplas linhas (ex.: Enviado e Entregue)."}
            </Typography>
          </Box>
          {loading ? (
            <Box sx={{ p: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Carregando eventos de webhook...
              </Typography>
            </Box>
          ) : isMobile ? (
            <Stack spacing={1} sx={{ p: 1 }}>
              {displayedRows.map((item) => (
                <Paper
                  key={item.group_key || item.id}
                  variant="outlined"
                  sx={{ p: 1.25, borderRadius: 2, bgcolor: "background.paper", cursor: "pointer" }}
                  onClick={() => openDetails(item)}
                >
                  <Stack spacing={0.75}>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTimeBR(getReliableEventTime(item))}
                    </Typography>
                    {renderEventBadges(item)}
                    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
                      {item.recipient_email || "-"}
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                      {item.subject || "-"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: "monospace", color: "text.secondary", wordBreak: "break-all" }}
                    >
                      {item.email_id || "-"}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
              {!displayedRows.length ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1, px: 1 }}>
                  Nenhum evento encontrado nesse período.
                </Typography>
              ) : null}
            </Stack>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Quando</TableCell>
                    <TableCell>{groupByMessage ? "Eventos" : "Evento"}</TableCell>
                    <TableCell>E-mail</TableCell>
                    <TableCell>Assunto</TableCell>
                    <TableCell>ID mensagem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedRows.map((item) => (
                    <TableRow
                      key={item.group_key || item.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => openDetails(item)}
                    >
                      <TableCell>{formatDateTimeBR(getReliableEventTime(item))}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{renderEventBadges(item)}</TableCell>
                      <TableCell>{item.recipient_email || "-"}</TableCell>
                      <TableCell>{item.subject || "-"}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>{item.email_id || "-"}</TableCell>
                    </TableRow>
                  ))}
                  {!displayedRows.length ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                          Nenhum evento encontrado nesse período.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Typography variant="body2" color="text.secondary">
            Página {page} de {totalPages} • Total: {eventsData?.total || 0}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent={{ xs: "space-between", sm: "flex-end" }}>
            <Button
              variant="outlined"
              disabled={loading || page <= 1}
              onClick={() => loadData(page - 1)}
            >
              Anterior
            </Button>
            <Button
              variant="outlined"
              disabled={loading || page >= totalPages}
              onClick={() => loadData(page + 1)}
            >
              Próxima
            </Button>
          </Stack>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}
      </Stack>

      <Dialog
        open={Boolean(selectedRow)}
        onClose={() => {
          setSelectedRow(null);
          setShowTechnicalDetails(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1 }}>Resumo do E-mail</DialogTitle>
        <DialogContent dividers>
          {selectedRow ? (() => {
            const events = getRowEvents(selectedRow);
            const latestPayload = parsePayload(events?.[0]?.payload_json);
            const latestData = latestPayload?.data || {};
            const payloadTo = Array.isArray(latestData?.to)
              ? latestData.to.join(", ")
              : latestData?.to || null;
            const payloadFrom = latestData?.from || null;
            const emailHtml =
              latestData?.html ||
              latestData?.body?.html ||
              latestData?.content?.html ||
              null;
            const emailText =
              latestData?.text ||
              latestData?.body?.text ||
              latestData?.content?.text ||
              null;
            const allEventTypes = Array.from(
              new Set(events.map((evt) => evt.event_type).filter(Boolean))
            );
            const hasIssue = allEventTypes.some((type) =>
              ["email.failed", "email.bounced", "email.complained", "email.suppressed"].includes(
                type
              )
            );
            const hasClicked = allEventTypes.includes("email.clicked");
            const hasOpened = allEventTypes.includes("email.opened");
            const hasDelivered = allEventTypes.includes("email.delivered");
            const hasSent = allEventTypes.includes("email.sent");
            const statusLabel = hasIssue
              ? "Atenção necessária"
              : hasClicked
              ? "Engajado (clicou no e-mail)"
              : hasOpened
              ? "Engajado (abriu o e-mail)"
              : hasDelivered
              ? "Entregue"
              : hasSent
              ? "Enviado"
              : "Em processamento";
            const statusSeverity = hasIssue
              ? "warning"
              : hasClicked || hasOpened
              ? "success"
              : "info";
            const nextStep = hasIssue
              ? "Revisar qualidade da lista e conteúdo do e-mail."
              : hasClicked
              ? "Lead com alto interesse. Bom momento para contato comercial."
              : hasOpened
              ? "Interesse inicial detectado. Considere reforço com CTA claro."
              : hasDelivered
              ? "E-mail entregue. Aguarde interação do destinatário."
              : "Acompanhe os próximos minutos para atualização do status.";

            return (
              <Stack spacing={1.5}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1.25}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Situação do Envio
                    </Typography>
                    <Alert severity={statusSeverity}>{statusLabel}</Alert>
                    <Typography variant="body2" color="text.secondary">
                      Próxima ação sugerida: {nextStep}
                    </Typography>
                    <Box sx={{ pt: 0.25 }}>{renderEventBadges(selectedRow)}</Box>
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1.25}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Dados do E-mail
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1.25,
                      }}
                    >
                      {renderInfo("Destinatário", selectedRow.recipient_email || payloadTo || "-")}
                      {renderInfo("Remetente", payloadFrom || "-")}
                      {renderInfo("Assunto", selectedRow.subject || latestData?.subject || "-")}
                      {renderInfo("Código da mensagem", selectedRow.email_id || "-", {
                        mono: true,
                        breakAll: true,
                      })}
                    </Box>
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Histórico do Envio
                    </Typography>
                    {events.map((evt, index) => (
                      <Box
                        key={`${evt.id || index}-${evt.event_type}`}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1.5,
                          p: 1,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                          {renderEventBadges({
                            ...selectedRow,
                            event_types: [evt.event_type],
                            event_type: evt.event_type,
                          })}
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTimeBR(getReliableEventTime(evt))}
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Conteúdo do E-mail Enviado
                    </Typography>
                    {emailHtml || emailText ? (
                      <Box
                        component="div"
                        sx={{
                          m: 0,
                          p: 1,
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: "rgba(15, 23, 42, 0.35)",
                          overflowX: "auto",
                          wordBreak: "break-word",
                          fontSize: 12,
                          lineHeight: 1.45,
                          maxHeight: 260,
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {emailText || "Conteúdo HTML disponível (resumo em texto não retornado)."}
                        </Typography>
                      </Box>
                    ) : (
                      <Alert severity="info">
                        O provedor não retorna o corpo completo do e-mail em todos os casos. Se você quiser histórico
                        completo do conteúdo enviado, podemos salvar isso no momento do disparo.
                      </Alert>
                    )}
                  </Stack>
                </Paper>

                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowTechnicalDetails((prev) => !prev)}
                  >
                    {showTechnicalDetails ? "Ocultar detalhes técnicos" : "Ver detalhes técnicos"}
                  </Button>
                </Stack>
                {showTechnicalDetails ? (
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Detalhes Técnicos (Suporte)
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 1,
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: "rgba(15, 23, 42, 0.35)",
                          overflowX: "auto",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          fontSize: 12,
                          lineHeight: 1.45,
                          maxHeight: 320,
                        }}
                      >
                        {JSON.stringify(latestPayload || {}, null, 2)}
                      </Box>
                    </Stack>
                  </Paper>
                ) : null}
              </Stack>
            );
          })() : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WebhookEmailCenter;
